import { useState, useEffect, useCallback, useRef } from 'react';
import type { MessageTemplate, Channel } from '@/types/messaging';
import { useModuleStore } from '@/stores/module-store';
import { useAppStore } from '@/stores/app-store';
import { idbCache } from '@/lib/cache/idb-cache';
import { setSkipCacheOnce } from '@/lib/api/proxy';
import { swrFetch, invalidateByPattern } from '@/lib/cache/swr';
import { cacheKeys, TTL } from '@/lib/cache/cache-keys';

/**
 * Hook for managing message templates.
 *
 * Only fetches when the TPA module is loaded AND authenticated.
 * This prevents API calls from firing before credentials are saved,
 * and ensures a re-fetch happens once authentication completes.
 *
 * @param channel  Optional channel filter (e.g. 'WHATSAPP')
 * @param options.lazy  When true, only loads from IDB cache on mount — no API
 *   calls until `refresh()` is called explicitly (e.g. when template picker opens).
 *   This prevents expensive template API calls during conversation loading.
 */
export function useTemplates(channel?: Channel, options?: { lazy?: boolean }) {
  const lazy = options?.lazy ?? false;
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const didFetchRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    if (!tpaModule || !tpaAuthenticated) return;

    const ext = useAppStore.getState().extensionName || '';
    if (!ext) return;

    setLoading(true);
    setError(null);
    try {
      const result = await swrFetch<MessageTemplate[]>({
        key: cacheKeys.templates(ext, channel),
        store: 'templates',
        fetcher: () => tpaModule.getTemplates({ channel }),
        ttl: TTL.TEMPLATES,
        onRevalidate: (fresh) => {
          setTemplates(fresh);
        },
      });
      setTemplates(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [tpaModule, tpaAuthenticated, channel]);

  /**
   * Lazy init: only load from IDB cache (no API calls).
   * Called on mount when lazy=true so templates are available instantly from cache
   * without triggering expensive API fetches during conversation loading.
   */
  const loadCacheOnly = useCallback(async () => {
    if (!tpaModule || !tpaAuthenticated) return;

    const ext = useAppStore.getState().extensionName || '';
    if (!ext) return;

    const cached = await idbCache.get<MessageTemplate[]>('templates', cacheKeys.templates(ext, channel));
    if (cached && cached.length > 0) {
      setTemplates(cached);
    }
  }, [tpaModule, tpaAuthenticated, channel]);

  /** Force-refresh templates bypassing all caches */
  const forceRefresh = useCallback(async () => {
    if (!tpaModule || !tpaAuthenticated) return;

    const ext = useAppStore.getState().extensionName || '';
    // Invalidate SWR + IDB template cache
    await invalidateByPattern(`templates:${ext}`, 'templates');
    // Bypass proxy cache for API calls during this refresh
    setSkipCacheOnce(true);

    setLoading(true);
    setError(null);
    try {
      const result = tpaModule.refreshTemplates
        ? await tpaModule.refreshTemplates()
        : await tpaModule.getTemplates({ channel });
      setTemplates(result);
      // Re-cache fresh data
      if (result.length > 0 && ext) {
        idbCache.set('templates', cacheKeys.templates(ext, channel), result, TTL.TEMPLATES).catch(() => {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setSkipCacheOnce(false);
      setLoading(false);
    }
  }, [tpaModule, tpaAuthenticated, channel]);

  useEffect(() => {
    if (lazy) {
      // Lazy mode: only show IDB-cached templates on mount (no API calls).
      // Full fetch happens when refresh() is called explicitly.
      loadCacheOnly();
    } else {
      fetchTemplates();
    }
  }, [lazy, fetchTemplates, loadCacheOnly]);

  // Convenience: auto-fetch on first refresh() call in lazy mode
  const refresh = useCallback(async () => {
    didFetchRef.current = true;
    return fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refresh, forceRefresh };
}
